'use client';

import type { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { recaptchaString, resetRecaptchaValues } from './helpers';

import { useGoogleRecaptchaCheckbox } from './useGoogleRecaptchaCheckbox';
import { useGoogleRecaptchaScore } from './useGoogleRecaptchaScore';
import { useIsMounted } from '../useIsMounted';
import { usePageLayout } from '../usePageLayout';
import '../../styles/recaptcha-global.css';

export type ResetRecaptchaParams = 'failed' | 'expired';

export interface IGoogleRecaptchaContextProps {
    executeRecaptchaScore: (action: string) => Promise<void>;
    recaptchaIdCheckbox?: number;
    recaptchaStatusCheckbox: 'idle' | 'loading' | 'ready' | 'error';
    useRecaptchaCheckbox: string | null;
    /** determines which checkbox should be rendered.  Value should match id of desired checkbox or null */
    setUseRecaptchaCheckbox: Dispatch<SetStateAction<string | null>>;
    renderRecaptchaCheckbox: (id: string) => Promise<void>;
    isRecaptchaCompleteCheckbox: boolean;
    handleRecaptcha: (action: string) => Promise<boolean>;
    resetRecaptchaCheckbox: (reason?: ResetRecaptchaParams) => void;
    recaptchaErrorCheckbox: string | null;
    setRecaptchaErrorCheckbox: Dispatch<SetStateAction<string | null>>;
    resetRecaptchaValues: () => void;
    siteKey?: string;
}

// Declare the global interface for the window object
declare global {
    interface Window {
        grecaptcha?: {
            enterprise: any;
        };
        automatedTestRun?: boolean;
    }
}

const GoogleRecaptchaContext = createContext<IGoogleRecaptchaContextProps | undefined>(undefined);

/**
 * top-level Google Recaptcha provider
 * - useGoogleRecaptcha to be used anywhere context available
 * - modeled after https://github.com/t49tran/react-google-recaptcha-v3
 */
export function GoogleRecaptchaProvider({ children }: { children: ReactNode }): ReactElement {
    const { executeRecaptchaScore, siteKey } = useGoogleRecaptchaScore();

    const {
        recaptchaIdCheckbox,
        recaptchaStatusCheckbox,
        useRecaptchaCheckbox,
        setUseRecaptchaCheckbox,
        renderRecaptchaCheckbox,
        isRecaptchaCompleteCheckbox,
        resetRecaptchaCheckbox,
        recaptchaErrorCheckbox,
        setRecaptchaErrorCheckbox,
    } = useGoogleRecaptchaCheckbox();

    /**
     * handles whether recaptcha uses score or challenge
     */
    const handleRecaptcha = useCallback(
        async (action: string) => {
            if (useRecaptchaCheckbox) {
                if (!isRecaptchaCompleteCheckbox) {
                    setRecaptchaErrorCheckbox(recaptchaString.checkboxAction);
                    return false;
                }
            } else {
                await executeRecaptchaScore(action);
            }

            return true;
        },
        [executeRecaptchaScore, isRecaptchaCompleteCheckbox, setRecaptchaErrorCheckbox, useRecaptchaCheckbox]
    );

    const value = useMemo(
        () => ({
            handleRecaptcha,
            executeRecaptchaScore,
            recaptchaIdCheckbox,
            recaptchaStatusCheckbox,
            useRecaptchaCheckbox,
            setUseRecaptchaCheckbox,
            renderRecaptchaCheckbox,
            isRecaptchaCompleteCheckbox,
            resetRecaptchaCheckbox,
            recaptchaErrorCheckbox,
            setRecaptchaErrorCheckbox,
            resetRecaptchaValues,
            siteKey,
        }),
        [
            handleRecaptcha,
            executeRecaptchaScore,
            recaptchaIdCheckbox,
            recaptchaStatusCheckbox,
            useRecaptchaCheckbox,
            setUseRecaptchaCheckbox,
            renderRecaptchaCheckbox,
            isRecaptchaCompleteCheckbox,
            resetRecaptchaCheckbox,
            recaptchaErrorCheckbox,
            setRecaptchaErrorCheckbox,
            siteKey,
        ]
    );

    return <GoogleRecaptchaContext.Provider value={value}>{children}</GoogleRecaptchaContext.Provider>;
}

/**
 * Recaptcha enabled by default.  To disable recaptcha, pass in a false param to this hook call.
 * Ideally controlled via a feature flag.
 *
 * In addition, internally, flags.enterpriseRecaptchaEnabled acts as a master switch that
 * controls all recaptcha instances
 */
export function useGoogleRecaptcha(isEnabled = true): IGoogleRecaptchaContextProps {
    const context = useContext(GoogleRecaptchaContext);
    const { isMounted } = useIsMounted();
    const { flags } = usePageLayout();

    if (context === undefined) {
        throw new Error('useGoogleRecaptcha must be used within a GoogleRecaptchaProvider');
    }

    const isRecaptchaEnabled = isMounted && !!flags.enterpriseRecaptchaEnabled && isEnabled;

    return isRecaptchaEnabled
        ? context
        : {
              ...context,
              handleRecaptcha: async () => true,
              setUseRecaptchaCheckbox: () => context.setRecaptchaErrorCheckbox(null),
          };
}
